
import { useState, useEffect } from 'react'
import './App.css';
import './components/legalPage.css';
import { Routes, Route, Navigate } from 'react-router-dom';
import { SignIn } from './components/SignIn';
import { SignOut } from './components/SignOut';
import Home from './components/Home';
import CareReportBuilderPage from './components/CareReportBuilderPage';
import CareReportFormPage from './components/CareReportFormPage';
import CaretakersPage from './components/CaretakersPage';
import ClientsPage from './components/ClientsPage';
import ClientDetailPage from './components/ClientDetailPage';
import InvoicingPage from './components/InvoicingPage';
import RecurringShiftsPage from './components/RecurringShiftsPage';
import PayrollPage from './components/PayrollPage';
import PayrollAllPage from './components/PayrollAllPage';
import EulaPage from './components/EulaPage';
import PrivacyPolicyPage from './components/PrivacyPolicyPage';
import { TopBar } from './components/TopBar';
import ImpersonationBanner from './components/ImpersonationBanner';

import { supabase } from './utils/supabase_config';
import { ShiftsService } from './services/supabase/shiftsService';
import { CaretakersService } from './services/supabase/caretakersService';
import { ClientsService } from './services/supabase/clientsService';
import { AppointmentsService } from './services/supabase/appointmentsService';
import { UserService } from './services/user/userService';
import { AuthService } from './services/auth/authService';
import { mapShiftRow } from './utils/shiftUtils';

function App() {

  const [user, setUser] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [authLoading, setAuthLoading] = useState(true);
  const [impersonatedUser, setImpersonatedUser] = useState(null);
  const [unauthorizedAttempt, setUnauthorizedAttempt] = useState(false);

  const [eventList, setEventList] = useState([]);
  const [appointmentList, setAppointmentList] = useState([]);
  const [clientList, setClientList] = useState([]);
  const [caretakerList, setCaretakerList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [userList, setUserList] = useState([]);
  const [currentStartDate, setCurrentStartDate] = useState(0);
  const [currentEndDate, setCurrentEndDate] = useState(0);
  const [currentStartTimestr, setCurrentStartTimestr] = useState('');
  const [currentEndTimestr, setCurrentEndTimestr] = useState('');
  const [displayDialog, setDisplayDialog] = useState(false);
  const [displayDeleteCaretakerDialog, setDisplayDeleteCaretakerDialog] = useState(false);
  const [displayDeleteClientDialog, setDisplayDeleteClientDialog] = useState(false);
  const [displayEditAppointmentDialog, setDisplayEditAppointmentDialog] = useState(false);
  const [eventClickedObject, setEventClickedObject] = useState({});
  const [caretakerToDeleteID, setCaretakerToDeleteID] = useState('');
  const [caretakerToDeleteName, setCaretakerToDeleteName] = useState('');
  const [clientToDeleteID, setClientToDeleteID] = useState('');
  const [clientToDeleteName, setClientToDeleteName] = useState('');
  const [userToDeleteID, setUserToDeleteID] = useState('');
  const [renderRepeatOptions, setRenderRepeatOptions] = useState(false);
  const [caretakerListNames, setCaretakerListNames] = useState([]);
  const [clientListNames, setClientListNames] = useState([]);
  const [filterButtons, setFilterButtons] = useState([]);
  const [filteredEventList, setFilteredEventList] = useState([]);
  const [masterEventList, setMasterEventList] = useState([]);
  const [filterButtonsArray, setFilterButtonsArray] = useState([]);
  const [caretakerMasterList, setCaretakerMasterList] = useState([]);
  const [caretakerMasterListNames, setCaretakerMasterListNames] = useState([]);
  const [aptClickedObject, setAptClickedObject] = useState({});
  const [aptToDeleteID, setAptToDeleteID] = useState('');
  const [displayConfirmDeleteAppointmentDialog, setDisplayConfirmDeleteAppointmentDialog] = useState(false);
  const [renderFlatFeeOptions, setRenderFlatFeeOptions] = useState(false);
  const [currentStartDateString, setCurrentStartDateString] = useState('');
  const [currentEndDateString, setCurrentEndDateString] = useState('');
  const [weeksLoaded, setWeeksLoaded] = useState([]);
  const [renderOvernightOptions, setRenderOvernightOptions] = useState(false);
  const [filterButtonsClient, setFilterButtonsClient] = useState([]);
  const [filterButtonsArrayClient, setFilterButtonsArrayClient] = useState([]);
  const [currWeekStartTimeSecs, setCurrWeekStartTimeSecs] = useState(0);
  const [currWeekEndTimeSecs, setCurrWeekEndTimeSecs] = useState(0);
  const [clientMasterList, setClientMasterList] = useState([]);
  const [clientMasterListNames, setClientMasterListNames] = useState([]);
  const [displayDeactivatedClientsDialog, setDisplayDeactivatedClientsDialog] = useState(false);
  const [deactivatedClientsList, setDeactivatedClientsList] = useState([]);
  const [deactivatedCaretakersList, setDeactivatedCaretakersList] = useState([]);

  const [invoiceDraft, setInvoiceDraft] = useState(() => {
    try {
      const saved = sessionStorage.getItem('invoiceDraft');
      return saved ? JSON.parse(saved) : { selectedClient: '', startDate: '', endDate: '', results: null };
    } catch { return { selectedClient: '', startDate: '', endDate: '', results: null }; }
  });
  const [payrollDraft, setPayrollDraft] = useState({ selectedCaretaker: '', startDate: '', endDate: '', results: null });
  const [payrollAllDraft, setPayrollAllDraft] = useState({ selectedCaretakers: [], startDate: '', endDate: '', results: null });
  const [qbConnected, setQbConnected] = useState(false);

  // Persist invoice draft across full page reloads (e.g. OAuth redirects)
  useEffect(() => {
    try { sessionStorage.setItem('invoiceDraft', JSON.stringify(invoiceDraft)); } catch {}
  }, [invoiceDraft]);

  // Subscribe to Supabase auth state changes
  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      setUser(session?.user ?? null);
    }).finally(() => setAuthLoading(false));

    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      setUser(session?.user ?? null);
    });

    return () => subscription.unsubscribe();
  }, []);

  // Load users once on mount
  useEffect(() => {
    InitialLoadUsers();
  }, []);

  // After user is set, verify they exist in the users table, then load app data
  useEffect(() => {
    if (user?.email) {
      UserService.getUserByEmail(user.email).then(record => {
        if (!record) {
          // Email not in users table — sign them out immediately
          setUnauthorizedAttempt(true);
          supabase.auth.signOut();
          return;
        }
        const adminStatus = !!record?.is_admin;
        setIsAdmin(adminStatus);
        InitialLoadShifts(adminStatus);
        loadClients();
        loadCaretakers();
      });
    } else {
      setIsAdmin(false);
    }
  }, [user]); // eslint-disable-line react-hooks/exhaustive-deps

  // When impersonating, the effective user/admin status reflects the impersonated user
  const effectiveUser    = impersonatedUser ?? user;
  const effectiveIsAdmin = impersonatedUser ? false : isAdmin;

  const startImpersonation = async (targetUser) => {
    setImpersonatedUser(targetUser);
    const now = Date.now();
    const windowStart = now - 35 * 24 * 60 * 60 * 1000;
    const windowEnd   = now + 65 * 24 * 60 * 60 * 1000;
    const rows = await ShiftsService.getShiftsForCaretakerInRange(targetUser.email, windowStart, windowEnd);
    const shifts = rows.map(mapShiftRow);
    setEventList(shifts);
    setMasterEventList(shifts);
    setWeeksLoaded([{ start: windowStart, end: windowEnd }]);
  };

  const stopImpersonation = async () => {
    setImpersonatedUser(null);
    const freshShifts = await InitialLoadShifts(true);
    if (freshShifts) applyFilterToMasterList(freshShifts);
  };

  /*
   *** Checks whether user logged in is admin or caretaker
  */
  const checkAdminPriviledge = () => {
    return effectiveIsAdmin;
  }

  /*
   *** Load the users from the DB. Needed to login.
  */
  const InitialLoadUsers = async () => {
    setLoading(true);
    try {
      const rows = await UserService.getUsers();
      const users = rows.map(row => ({
        name: row.name,
        email: row.email,
        type: row.type,
        docID: row.id,
        isDeactivated: row.is_deactivated
      }));
      setUserList(users);
    } catch (error) {
      console.error('Error loading users:', error);
    } finally {
      setLoading(false);
    }
  }

  /*
   *** Load shifts to the calendar.
   *** Fetches a bounded window: 35 days before today through 65 days ahead.
   *** Wider ranges are loaded on-demand when the user navigates (Perf #3).
  */
  async function InitialLoadShifts(adminOverride) {
    const effectiveAdmin = adminOverride !== undefined ? adminOverride : isAdmin;
    const now = Date.now();
    const windowStart = now - 35 * 24 * 60 * 60 * 1000;
    const windowEnd   = now + 65 * 24 * 60 * 60 * 1000;
    try {
      let rows;
      if (effectiveAdmin) {
        rows = await ShiftsService.getShiftsForDateRange(windowStart, windowEnd);
      } else {
        rows = await ShiftsService.getShiftsForCaretakerInRange(effectiveUser.email, windowStart, windowEnd);
      }
      const shifts = rows.map(mapShiftRow);
      setEventList(shifts);
      setMasterEventList(shifts);
      setWeeksLoaded([{ start: windowStart, end: windowEnd }]);
      return shifts;
    } catch (error) {
      console.error('Error loading shifts:', error);
    }
  }

  /*
   *** Sign in with Google via Supabase OAuth (redirect flow)
  */
  const signInWithGoogle = async () => {
    try {
      await AuthService.signInWithGoogle();
    } catch (error) {
      console.error('ERROR: ' + error.message);
    }
  }

  /*
   *** Add shift to the main calendar. Supports repeating shifts and flat fees.
  */
  async function addShift(shiftsToAdd) {
    const shiftsPayload = shiftsToAdd.map(shift => {
      const caretakerInUserList = userList.find(e => e.name === shift.chosenCaretaker);
      const mileage = shift.shiftMileage === undefined || shift.shiftMileage === '' ? 0 : shift.shiftMileage;

      return {
        client: shift.chosenClient,
        caretaker: shift.chosenCaretaker,
        start_time: shift.startDatetime,
        end_time: shift.endDatetime,
        comments: shift.shiftComments,
        color: shift.caretakerColor,
        edited_start_time: '',
        edited_end_time: '',
        pretty_edited_start_time: '',
        pretty_edited_end_time: '',
        caretaker_email: caretakerInUserList?.email ?? '',
        repeating_shift: shift.shiftRepeat === true,
        repeating_shift_id: shift.shiftRepeat === true ? shift.shiftRepeatID : null,
        start_time_secs: shift.startTimeSecs,
        is_flat_fee: shift.isFlatFeeShift,
        flat_fee_amount: shift.flatFeeCaretaker ? Number(shift.flatFeeCaretaker) : null,
        flat_fee_amount_client: shift.flatFeeClient ? Number(shift.flatFeeClient) : null,
        shift_mileage: mileage
      };
    });

    await ShiftsService.addShiftsBatch(shiftsPayload);
    const freshShifts = await InitialLoadShifts();
    if (freshShifts) applyFilterToMasterList(freshShifts);
  }

  /*
   *** Add a caretaker to the caretaker table.
  */
  const addCaretaker = async (caretakerName, caretakerEmail, caretakerPay, caretakerColor) => {
    await CaretakersService.createCaretaker({
      name: caretakerName,
      email: caretakerEmail,
      pay: { '0': caretakerPay },
      color: caretakerColor,
      clients: []
    });

    const rows = await CaretakersService.getAll();
    const masterCaretakers = [];
    const masterCaretakersNames = [];
    const caretakers = [];
    const caretakersNames = [];

    rows.forEach(row => {
      const record = {
        name: row.name,
        email: row.email,
        pay: row.pay,
        color: row.color,
        docID: row.id,
        isDeactivated: row.is_deactivated,
        deactivationTime: row.deactivation_time
      };
      masterCaretakers.push(record);
      masterCaretakersNames.push(row.name);
      if (!row.is_deactivated) {
        caretakers.push(record);
        caretakersNames.push(row.name);
      }
    });

    setCaretakerList(caretakers);
    setCaretakerListNames(caretakersNames);
    setCaretakerMasterList(masterCaretakers);
    setCaretakerMasterListNames(masterCaretakersNames);
  }

  /*
   *** Reload all caretaker lists (active, master, deactivated).
   *** Used by CaretakersPage and Home on initial mount.
  */
  const loadCaretakers = async () => {
    const rows = await CaretakersService.getAll();
    const caretakers = [];
    const caretakersNames = [];
    const masterCaretakers = [];
    const masterCaretakersNames = [];
    const deactivatedCaretakers = [];

    rows.forEach(row => {
      const record = {
        name: row.name,
        clients: row.clients,
        pay: row.pay,
        color: row.color,
        email: row.email,
        isDeactivated: row.is_deactivated,
        deactivationTime: row.deactivation_time,
        docID: row.id
      };
      masterCaretakersNames.push(row.name);
      masterCaretakers.push(record);
      if (!row.is_deactivated) {
        caretakersNames.push(row.name);
        caretakers.push(record);
      } else {
        deactivatedCaretakers.push(record);
      }
    });

    setCaretakerList(caretakers);
    setCaretakerListNames(caretakersNames);
    setCaretakerMasterList(masterCaretakers);
    setCaretakerMasterListNames(masterCaretakersNames);
    setDeactivatedCaretakersList(deactivatedCaretakers);
  };

  /*
   *** Reload all client lists (active, master, deactivated).
   *** Used by ClientsPage and Home on initial mount.
  */
  const loadClients = async () => {
    const rows = await ClientsService.getAll();
    const clients = [];
    const clientNames = [];
    const masterClients = [];
    const masterClientNames = [];
    const deactivatedClients = [];

    rows.forEach(row => {
      const record = {
        name: row.name,
        rate: row.rate,
        docID: row.id,
        isDeactivated: row.is_deactivated,
        deactivationTime: row.deactivation_time
      };
      masterClientNames.push(record.name);
      masterClients.push(record);
      if (!record.isDeactivated) {
        clientNames.push(record.name);
        clients.push(record);
      } else {
        deactivatedClients.push(record);
      }
    });

    setClientList(clients);
    setClientListNames(clientNames);
    setClientMasterList(masterClients);
    setClientMasterListNames(masterClientNames);
    setDeactivatedClientsList(deactivatedClients);
  };

  /*
   *** Adds a Client to the client table
  */
  const addClient = async (clientName, clientRate) => {
    await ClientsService.createClient({
      name: clientName,
      rate: { '0': clientRate }
    });

    const rows = await ClientsService.getAll();
    const clients = rows.map(row => ({
      name: row.name,
      rate: row.rate,
      docID: row.id
    }));
    setClientList(clients);
  }

  /*
   *** Adds a user to user table. Called from addCaretaker.
  */
  async function addUser(userName, userEmail, userType) {
    await UserService.createUser({
      name: userName,
      email: userEmail,
      type: userType
    });
    await InitialLoadUsers();
  }

  /*
   *** Get time from datetime object and convert to 12hr std time
  */
  const prettifyTime = (time) => {
    let timeAfterSplit = time.split('T')[1];
    let hourVal = timeAfterSplit.split(':')[0];
    let minVal = timeAfterSplit.split(':')[1];
    let isPM = false;
    if (hourVal >= 13) { hourVal -= 12; isPM = true; }
    if (hourVal >= 12) { isPM = true; }
    return isPM ? `${hourVal}:${minVal} PM` : `${hourVal}:${minVal} AM`;
  }

  /*
   *** Add appointments to appointment calendar.
  */
  const addAppointment = async (clientName, startDatetime, endDatetime, commentDetails, addedByUser, appointmentLabel) => {
    await AppointmentsService.createAppointment({
      client: clientName,
      start_time: startDatetime,
      end_time: endDatetime,
      details: commentDetails,
      added_by: addedByUser,
      apt_label: appointmentLabel
    });
    await InitialLoadAppointments();
  }

  /*
   *** Caretaker side shift time editing
  */
  async function editTimeWorked(startStr, editedStartTime, editedEndTime,
    ogCaretakerForShift, clientForShift, originalStartTime, originalEndTime) {

    // Find the shift by caretaker + client + start prefix
    const matches = await ShiftsService.findShiftByStartPrefix(
      user.email,
      clientForShift,
      startStr
    );

    for (const shift of matches) {
      if (shift.caretaker === ogCaretakerForShift) {
        await ShiftsService.editTimeWorked(shift.docID, {
          edited_start_time: editedStartTime,
          edited_end_time: editedEndTime,
          original_start_time: originalStartTime,
          original_end_time: originalEndTime,
          start_time: editedStartTime,
          end_time: editedEndTime
        });
      }
    }

    await InitialLoadShifts();
  }

  /*
   *** Admin shift info editing (caretaker, client, times, fees, mileage).
   *** Always updates only the single selected shift by its docID.
  */
  async function editInfoForShift(shiftDocID, updatedCaretakerForShift, updatedClientForShift,
    adjustedStartTime, adjustedEndTime, updatedFlatFeeAmount, updatedMileage, updatedFlatFeeAmountClient) {

    const updatedCaretakerObj = caretakerList.find(e => e.name === updatedCaretakerForShift);
    const updatedCaretakerEmail = updatedCaretakerObj?.email ?? '';

    const updates = {
      caretaker: updatedCaretakerForShift,
      caretaker_email: updatedCaretakerEmail,
      color: updatedCaretakerObj?.color ?? '',
      client: updatedClientForShift,
      start_time: adjustedStartTime,
      end_time: adjustedEndTime,
      start_time_secs: new Date(adjustedStartTime).getTime(),
      shift_mileage: updatedMileage,
      edited_start_time: '',
      edited_end_time: '',
    };
    if (updatedFlatFeeAmount)       updates.flat_fee_amount        = updatedFlatFeeAmount;
    if (updatedFlatFeeAmountClient) updates.flat_fee_amount_client = updatedFlatFeeAmountClient;

    await ShiftsService.editShiftInfo(shiftDocID, updates);
    const freshShifts = await InitialLoadShifts();
    if (freshShifts) applyFilterToMasterList(freshShifts);
  }

  const applyFilterToMasterList = (list = masterEventList) => {
    const filteredNamesOnly = filterButtonsArray.filter(e => e.filterValue === 'checked').map(f => f.name);
    const foundShifts = list.filter(s => filteredNamesOnly.includes(s.caretaker));
    setEventList(foundShifts);
  }

  async function InitialLoadAppointments() {
    try {
      const rows = await AppointmentsService.getAll();
      const apts = rows.map(row => ({
        client: row.client,
        start: row.start_time,
        end: row.end_time,
        details: row.details,
        addedBy: row.added_by,
        aptLabel: row.apt_label,
        aptID: row.id
      }));
      setAppointmentList(apts);
    } catch (error) {
      console.error('Error loading appointments:', error);
    }
  }

  async function deleteShift(arg) {
    await ShiftsService.deleteShift(arg.event.extendedProps.docID);
    const freshShifts = await InitialLoadShifts();
    if (freshShifts) applyFilterToMasterList(freshShifts);
  }

  async function deleteApt() {
    await AppointmentsService.deleteAppointment(aptToDeleteID);
    setAptToDeleteID('');
    await InitialLoadAppointments();
  }

  async function editAptNotes(aptID, newDetails) {
    await AppointmentsService.updateAppointment(aptID, { details: newDetails });
    await InitialLoadAppointments();
  }

  async function deleteFutureShifts(arg) {
    await ShiftsService.deleteFutureShifts(
      arg.event.extendedProps.repeatingShiftID,
      arg.event.extendedProps.startTimeSecs
    );
    const freshShifts = await InitialLoadShifts();
    if (freshShifts) applyFilterToMasterList(freshShifts);
  }

  async function restoreClient(clientObj) {
    await ClientsService.restore(clientObj.docID);
    const newDeactivList = deactivatedClientsList.filter(e => e.docID !== clientObj.docID);
    const newActivList = [...clientList, clientObj];
    setDeactivatedClientsList(newDeactivList);
    setClientList(newActivList);
    alert('' + clientObj.name + " successfully restored. Click the 'show deactivated clients' button to see changes.");
  }

  async function restoreCaretaker(caretakerObj) {
    await CaretakersService.restore(caretakerObj.docID);
    await UserService.createUser({
      email: caretakerObj.email,
      name: caretakerObj.name,
      type: 'caretaker'
    });

    const newDeactivList = deactivatedCaretakersList.filter(e => e.docID !== caretakerObj.docID);
    const newActivList = [...caretakerList, caretakerObj];
    setDeactivatedCaretakersList(newDeactivList);
    setCaretakerList(newActivList);
    await InitialLoadUsers();
    alert('' + caretakerObj.name + " successfully restored. Click the 'show deactivated clients' button to see changes.");
  }

  /*
   *** Check that the logged-in user is allowed to access the app
  */
  const checkEmail = () => {
    if (userList.some(e => e.email === user?.email && e.isDeactivated !== true)) {
      return true;
    }
    AuthService.signOut();
    return false;
  }

  const getTimeAndDate = async (startDate, endDate, startTimestr, endTimestr) => {
    setCurrentStartDate(startDate);
    setCurrentEndDate(endDate);
    setCurrentStartTimestr(startTimestr);
    setCurrentEndTimestr(endTimestr);

    // Lazy-load shifts for date ranges not yet fetched
    const rangeStart = new Date(startTimestr).getTime();
    const rangeEnd   = new Date(endTimestr).getTime();
    if (!rangeStart || !rangeEnd) return;

    const alreadyCovered = weeksLoaded.some(r => r.start <= rangeStart && r.end >= rangeEnd);
    if (!alreadyCovered) {
      try {
        let newRows;
        if (isAdmin) {
          newRows = await ShiftsService.getShiftsForDateRange(rangeStart, rangeEnd);
        } else {
          newRows = await ShiftsService.getShiftsForCaretakerInRange(effectiveUser.email, rangeStart, rangeEnd);
        }
        const newShifts = newRows.map(mapShiftRow);
        setMasterEventList(prev => {
          const existingIds = new Set(prev.map(s => s.docID));
          const deduped = newShifts.filter(s => !existingIds.has(s.docID));
          return [...prev, ...deduped];
        });
        setWeeksLoaded(prev => [...prev, { start: rangeStart, end: rangeEnd }]);
      } catch (error) {
        console.error('Error lazy-loading shifts:', error);
      }
    }
  }

  if (loading) {
    return <h1>Loading...</h1>
  }

  const adminFallback = authLoading ? null : <Navigate to="/" />;

  return (
    <div className={`App${impersonatedUser ? ' App--impersonating' : ''}`}>
      <a name="homeTarget"></a>
      { !user && <SignIn signInWithGoogle={signInWithGoogle} unauthorizedAttempt={unauthorizedAttempt}/> }

      <SignOut />
      <ImpersonationBanner
        impersonatedUser={impersonatedUser}
        onExit={stopImpersonation}
      />
      {user && checkEmail() && (
        <TopBar
          isAdmin={effectiveIsAdmin}
          isRealAdmin={isAdmin}
          userList={userList}
          impersonatedUser={impersonatedUser}
          onImpersonate={startImpersonation}
          topOffset={impersonatedUser ? '36px' : undefined}
        />
      )}
      {user && checkEmail() && (
        <Routes>
          <Route path="/" element={
            <Home
              checkAdminPriviledge={checkAdminPriviledge}
              currentStartDate={currentStartDate}
              currentEndDate={currentEndDate}
              eventList={eventList}
              setEventList={setEventList}
              getTimeAndDate={getTimeAndDate}
              displayDialog={displayDialog}
              setDisplayDialog={setDisplayDialog}
              displayDeleteCaretakerDialog={displayDeleteCaretakerDialog}
              displayDeleteClientDialog={displayDeleteClientDialog}
              setDisplayDeleteCaretakerDialog={setDisplayDeleteCaretakerDialog}
              setDisplayDeleteClientDialog={setDisplayDeleteClientDialog}
              setEventClickedObject={setEventClickedObject}
              prettifyTime={prettifyTime}
              user={effectiveUser}
              clientList={clientList}
              setClientList={setClientList}
              caretakerList={caretakerList}
              setCaretakerList={setCaretakerList}
              userList={userList}
              setUserList={setUserList}
              setLoading={setLoading}
              eventClickedObject={eventClickedObject}
              editTimeWorked={editTimeWorked}
              addShift={addShift}
              deleteShift={deleteShift}
              deleteFutureShifts={deleteFutureShifts}
              addCaretaker={addCaretaker}
              setCaretakerToDeleteID={setCaretakerToDeleteID}
              setCaretakerToDeleteName={setCaretakerToDeleteName}
              caretakerToDeleteName={caretakerToDeleteName}
              caretakerToDeleteID={caretakerToDeleteID}
              setClientToDeleteID={setClientToDeleteID}
              setClientToDeleteName={setClientToDeleteName}
              clientToDeleteID={clientToDeleteID}
              clientToDeleteName={clientToDeleteName}
              addAppointment={addAppointment}
              appointmentList={appointmentList}
              setAppointmentList={setAppointmentList}
              addUser={addUser}
              userToDeleteID={userToDeleteID}
              setUserToDeleteID={setUserToDeleteID}
              addClient={addClient}
              renderRepeatOptions={renderRepeatOptions}
              setRenderRepeatOptions={setRenderRepeatOptions}
              setCaretakerListNames={setCaretakerListNames}
              caretakerListNames={caretakerListNames}
              editInfoForShift={editInfoForShift}
              filterButtons={filterButtons}
              setFilterButtons={setFilterButtons}
              filteredEventList={filteredEventList}
              setFilteredEventList={setFilteredEventList}
              masterEventList={masterEventList}
              setMasterEventList={setMasterEventList}
              setFilterButtonsArray={setFilterButtonsArray}
              caretakerMasterList={caretakerMasterList}
              setCaretakerMasterList={setCaretakerMasterList}
              caretakerMasterListNames={caretakerMasterListNames}
              setCaretakerMasterListNames={setCaretakerMasterListNames}
              displayEditAppointmentDialog={displayEditAppointmentDialog}
              setDisplayEditAppointmentDialog={setDisplayEditAppointmentDialog}
              setAptClickedObject={setAptClickedObject}
              aptClickedObject={aptClickedObject}
              setAptToDeleteID={setAptToDeleteID}
              deleteApt={deleteApt}
              displayConfirmDeleteAppointmentDialog={displayConfirmDeleteAppointmentDialog}
              setDisplayConfirmDeleteAppointmentDialog={setDisplayConfirmDeleteAppointmentDialog}
              editAptNotes={editAptNotes}
              renderFlatFeeOptions={renderFlatFeeOptions}
              setRenderFlatFeeOptions={setRenderFlatFeeOptions}
              currentStartDateString={currentStartDateString}
              setCurrentStartDateString={setCurrentStartDateString}
              currentEndDateString={currentEndDateString}
              setCurrentEndDateString={setCurrentEndDateString}
              clientListNames={clientListNames}
              setClientListNames={setClientListNames}
              weeksLoaded={weeksLoaded}
              setWeeksLoaded={setWeeksLoaded}
              renderOvernightOptions={renderOvernightOptions}
              setRenderOvernightOptions={setRenderOvernightOptions}
              filterButtonsClient={filterButtonsClient}
              setFilterButtonsClient={setFilterButtonsClient}
              setFilterButtonsArrayClient={setFilterButtonsArrayClient}
              filterButtonsArrayClient={filterButtonsArrayClient}
              currWeekStartTimeSecs={currWeekStartTimeSecs}
              setCurrWeekStartTimeSecs={setCurrWeekStartTimeSecs}
              currWeekEndTimeSecs={currWeekEndTimeSecs}
              setCurrWeekEndTimeSecs={setCurrWeekEndTimeSecs}
              clientMasterList={clientMasterList}
              clientMasterListNames={clientMasterListNames}
              setClientMasterList={setClientMasterList}
              setClientMasterListNames={setClientMasterListNames}
              displayDeactivatedClientsDialog={displayDeactivatedClientsDialog}
              setDisplayDeactivatedClientsDialog={setDisplayDeactivatedClientsDialog}
              deactivatedClientsList={deactivatedClientsList}
              setDeactivatedClientsList={setDeactivatedClientsList}
              deactivatedCaretakersList={deactivatedCaretakersList}
              setDeactivatedCaretakersList={setDeactivatedCaretakersList}
              restoreClient={restoreClient}
              restoreCaretaker={restoreCaretaker}
              loadCaretakers={loadCaretakers}
              loadClients={loadClients}
              qbConnected={qbConnected}
              onQbStatusChange={setQbConnected}
            />
          } />
          <Route
            path="/clients"
            element={
              <ClientsPage
                isAdmin={isAdmin}
                clientList={clientList}
                addClient={addClient}
                loadClients={loadClients}
                setClientToDeleteID={setClientToDeleteID}
                setClientToDeleteName={setClientToDeleteName}
                displayDeleteClientDialog={displayDeleteClientDialog}
                setDisplayDeleteClientDialog={setDisplayDeleteClientDialog}
                clientToDeleteName={clientToDeleteName}
                clientToDeleteID={clientToDeleteID}
                clientMasterList={clientMasterList}
                deactivatedClientsList={deactivatedClientsList}
                restoreClient={restoreClient}
                displayDeactivatedClientsDialog={displayDeactivatedClientsDialog}
                setDisplayDeactivatedClientsDialog={setDisplayDeactivatedClientsDialog}
              />
            }
          />
          <Route
            path="/caretakers"
            element={isAdmin
              ? <CaretakersPage
                  isAdmin={isAdmin}
                  addCaretaker={addCaretaker}
                  addUser={addUser}
                  caretakerList={caretakerList}
                  loadCaretakers={loadCaretakers}
                  InitialLoadShifts={InitialLoadShifts}
                  setCaretakerToDeleteID={setCaretakerToDeleteID}
                  setCaretakerToDeleteName={setCaretakerToDeleteName}
                  displayDeleteCaretakerDialog={displayDeleteCaretakerDialog}
                  setDisplayDeleteCaretakerDialog={setDisplayDeleteCaretakerDialog}
                  caretakerToDeleteName={caretakerToDeleteName}
                  caretakerToDeleteID={caretakerToDeleteID}
                  setUserToDeleteID={setUserToDeleteID}
                  userToDeleteID={userToDeleteID}
                  userList={userList}
                  caretakerMasterList={caretakerMasterList}
                  deactivatedCaretakersList={deactivatedCaretakersList}
                  restoreCaretaker={restoreCaretaker}
                  masterEventList={masterEventList}
                />
              : adminFallback}
          />
          <Route
            path="/care-report"
            element={<CareReportFormPage user={effectiveUser} isAdmin={effectiveIsAdmin} caretakerList={caretakerList} />}
          />
          <Route
            path="/care-report-builder"
            element={isAdmin
              ? <CareReportBuilderPage clientList={clientList} isAdmin={isAdmin} />
              : adminFallback}
          />
          <Route
            path="/client/:clientName"
            element={<ClientDetailPage caretakerList={caretakerList} />}
          />
          <Route
            path="/invoicing"
            element={isAdmin
              ? <InvoicingPage clientList={clientList} caretakerList={caretakerList} draft={invoiceDraft} onDraftChange={setInvoiceDraft} qbConnected={qbConnected} />
              : adminFallback}
          />
          <Route
            path="/recurring-shifts"
            element={isAdmin
              ? <RecurringShiftsPage clientList={clientList} caretakerList={caretakerList} />
              : adminFallback}
          />
          <Route
            path="/payroll"
            element={isAdmin
              ? <PayrollPage clientList={clientList} caretakerList={caretakerList} draft={payrollDraft} onDraftChange={setPayrollDraft} qbConnected={qbConnected} />
              : adminFallback}
          />
          <Route
            path="/payroll-all"
            element={isAdmin
              ? <PayrollAllPage clientList={clientList} caretakerList={caretakerList} draft={payrollAllDraft} onDraftChange={setPayrollAllDraft} />
              : adminFallback}
          />
          <Route path="/eula" element={<EulaPage />} />
          <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
        </Routes>
      )}
      <footer className="appFooter">
        <span>© {new Date().getFullYear()} Effectual Tools</span>
        <a href="/eula">End-User License Agreement</a>
        <a href="/privacy-policy">Privacy Policy</a>
        <a href="mailto:daniel@effectual.tools">Contact</a>
      </footer>
    </div>
  );
}

export default App;
